import { Component, inject, signal } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { OrderFiltersForm, StatusOption } from '@lc/ui';
import {
  PhotoOrder, PhotoOrderService, UIConstants,
  MarketingOrdersAreasService, ToasterService, PhotoOrderStatus, PromptDialogService,
} from '@lc/core';
import { BehaviorSubject, combineLatest, EMPTY, merge, Observable } from 'rxjs';
import {
  catchError, debounceTime, distinctUntilChanged, finalize, map, skip, startWith, switchMap, tap,
} from 'rxjs/operators';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { NgClass, AsyncPipe, DatePipe } from '@angular/common';
import { OrderFiltersComponent } from '@lc/ui/lib/order-filters/components/order-filters/order-filters.component';
import { PhotoOrderStatusPipe } from '@lc/core/lib/pipes/photo-order-status.pipe';
import { AddressPipe } from '@lc/core/lib/pipes/address.pipe';
import { PageRequestForm } from '../models/page-request.model';
import { PhotoAppProvidersService } from '../photo-app-providers.service';
import { PhotoAppProvider } from '../models/photo-app-provider.model';

/**
 * This ViewModel is the main view model for this UI.
 * It contains the agency information as well as the
 * viewModels used for each row in the table.
 */
export class ViewModel {
  readonly photoOrderViewModels: PhotoOrderViewModel[];

  constructor(readonly photoOrders: PhotoOrder[]) {
    this.photoOrderViewModels = photoOrders.map((photoOrder) => new PhotoOrderViewModel(photoOrder));
  }
}

/**
 * This ViewModel is used by each record in the table and signifies
 * the actions that can be performed on each photoOrder.
 */
export class PhotoOrderViewModel {
  readonly canClick: boolean;
  readonly canStart: boolean;
  readonly canCancel: boolean;
  readonly canComplete: boolean;
  readonly hasActions: boolean;

  constructor(readonly photoOrder: PhotoOrder) {
    this.canClick = photoOrder.status !== PhotoOrderStatus.CANCELLED;
    this.canStart = photoOrder.status === PhotoOrderStatus.OPEN;
    this.canCancel = photoOrder.status !== PhotoOrderStatus.CANCELLED;
    this.canComplete = photoOrder.status === PhotoOrderStatus.SUBMITTING;
    this.hasActions = this.canStart || this.canCancel || this.canComplete;
  }
}

@Component({
  selector: 'lc-appointment-list',
  templateUrl: './appointment-list.component.html',
  styleUrls: ['./appointment-list.component.scss'],
  imports: [
    OrderFiltersComponent,
    MatProgressSpinner,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatTooltip,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgClass,
    MatPaginator,
    MatButton,
    RouterLink,
    AsyncPipe,
    DatePipe,
    PhotoOrderStatusPipe,
    AddressPipe,
  ],
})
export class AppointmentListComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialogService = inject(PromptDialogService);
  private photoOrderService = inject(PhotoOrderService);
  private toasterService = inject(ToasterService);
  private readonly providers = inject(PhotoAppProvidersService);
  readonly selectedPhotographer = signal<PhotoAppProvider | undefined>(undefined);
  readonly hasMultiplePhotographers = signal(false);

  /** Whether or not the UI is loading and should display the indicator */
  isLoading: boolean;
  readonly loadError = signal(false);
  totalCount: number;

  /** The areas that are available on the filters */
  readonly areas$: Observable<string[]>;

  readonly pageRequestForm = new PageRequestForm();

  /** The main viewModel used to represent the UI */
  readonly viewModel$: Observable<ViewModel>;

  /** The columns that display in the table */
  readonly columns = ['order-number', 'order-date', 'photographer', 'agent', 'scheduled-date', 'address', 'brand', 'area', 'status', 'action'];

  /** The form used by the filters */
  readonly filtersForm = new OrderFiltersForm({
    statusOptions: [
      new StatusOption('To Do', '#E1E8ED', [PhotoOrderStatus.OPEN]),
      new StatusOption('In Progress', '#F4B124', [PhotoOrderStatus.SERVICING]),
      new StatusOption('Submitting', '#3989C9', [PhotoOrderStatus.SUBMITTING]),
      new StatusOption('Done', '#657786', [PhotoOrderStatus.CLOSED]),
    ],
  });

  /** Triggers a manual reload of the photos */
  private readonly reload$ = new BehaviorSubject<true>(true);

  constructor() {
    const areaService = inject(MarketingOrdersAreasService);

    const pageChanges$ = this.pageRequestForm.valueChanges.pipe(
      startWith(this.pageRequestForm.value),
      debounceTime(200),
      map(() => this.pageRequestForm.value),
    );

    this.areas$ = areaService.getAreas$();
    this.filtersForm.userFilters = 'agent';

    // Subscribe to route changes
    const queryParams$ = this.route.queryParams.pipe(map((params) => this.parseQueryParams(params)));
    // Set defaults of filter form prior to passing in params to photoOrderService
    this.setDefaultPreferences();
    this.parseQueryParams(this.route.snapshot.queryParams);

    // Subscribe to the reload behavior subject, which emits immediately upon entering page and anytime there are updates
    const filterChanges$ = merge(this.reload$, this.filtersForm.search.valueChanges.pipe(
      startWith(this.filtersForm.search.value),
      distinctUntilChanged(),
      skip(1),
      debounceTime(300),
      tap(() => this.pageRequestForm.pageNumber.setValue(1, { emitEvent: false })),
    ));

    this.viewModel$ = combineLatest([this.route.paramMap, pageChanges$, queryParams$, filterChanges$]).pipe(
      switchMap(([params]) => {
        this.isLoading = true;
        this.loadError.set(false);
        return this.providers.list$().pipe(
          switchMap((providers) => {
            const photographer = providers.find((provider) => provider.ids.includes(params.get('photographerId')));
            if (!photographer) {
              this.router.navigate(['/']);
              return EMPTY;
            }
            this.selectedPhotographer.set(photographer);
            this.hasMultiplePhotographers.set(providers.length > 1);
            return this.photoOrderService.retrievePhotoOrders({
              ...this.filtersForm.getFilters(), photographerId: photographer.id,
            }, this.pageRequestForm.value);
          }),
          map((response) => {
            this.pageRequestForm.totalCount = response.totalCount;
            return new ViewModel(response.orders);
          }),
          tap(() => this.updateQueryParams()),
          catchError(() => {
            this.loadError.set(true);
            return EMPTY;
          }),
          finalize(() => this.isLoading = false),
        );
      }),
    );
  }

  onFilter() {
    this.pageRequestForm.pageNumber.setValue(1);
  }

  retry() {
    this.reload$.next(true);
  }

  // Sets the default values for filters form
  private setDefaultPreferences() {
    // reset filter form
    this.filtersForm.reset();

    // Default the date to the last 30 days
    const customDateOption = this.filtersForm.dateOptions.find((dateOption) => dateOption.text === 'Custom');
    const now = new Date();
    customDateOption.startDate ??= new Date(now.setDate(now.getDate() - 30));
    customDateOption.endDate ??= new Date();
    this.filtersForm.selectedDateOption = customDateOption;

    // Set default initial status to TO DO ( OPEN ), IN PROGRESS ( SERVICING ), SUBMITTING
    this.filtersForm.setByStatus([PhotoOrderStatus.OPEN, PhotoOrderStatus.SERVICING, PhotoOrderStatus.SUBMITTING]);
  }

  async onClick(photoOrderVm: PhotoOrderViewModel) {
    if (photoOrderVm.canStart) {
      return await this.start(photoOrderVm.photoOrder);
    } if (photoOrderVm.canClick) {
      return this.navigateToOrder(photoOrderVm.photoOrder);
    }
  }

  /** Prompts the user and then proceeds to start the order */
  async start(photoOrder: PhotoOrder) {
    // Prompt user to confirm
    const message = 'This confirms you are accepting this order and will confirm the preferred date/time with the Agent. Do you want to continue?';
    const response = await this.dialogService.openPrompt(UIConstants.CONFIRM, message, UIConstants.YES, [UIConstants.NO]);
    if (response?.text !== UIConstants.YES) { return; } // Don't do anything if they did not click yes

    // Make a call to start order
    await this.photoOrderService.startOrder(photoOrder)
      .then(() => this.navigateToOrder(photoOrder))
      .catch(() => this.toasterService.showError('Failed to start order'));
  }

  /** Prompt the user and then proceeds to complete the order */
  async complete(photoOrder: PhotoOrder) {
    // Prompt user to confirm
    const message = 'This confirms you are completing this order. Do you want to continue?';
    const response = await this.dialogService.openPrompt(UIConstants.CONFIRM, message, UIConstants.YES, [UIConstants.NO]);
    if (response?.text !== UIConstants.YES) { return; } // Don't do anything if they did not click yes

    // Make a call to complete order
    await this.photoOrderService.completeOrder(photoOrder)
      .then(() => this.reload$.next(true))
      .catch(() => this.toasterService.showError('Failed to complete order'));
  }

  /** Prompts user and then proceeds to cancel the order */
  async cancel(photoOrder: PhotoOrder) {
    // Prompt user to confirm
    const message = 'Are you sure you want to cancel this photography order?';
    const response = await this.dialogService.openPrompt(UIConstants.CONFIRM, message, UIConstants.YES, [UIConstants.NO]);
    if (response?.text !== UIConstants.YES) { return; } // Don't do anything if they did not click yes

    // Make a call to complete order
    await this.photoOrderService.cancelOrder(photoOrder)
      .then(() => this.reload$.next(true))
      .catch(() => this.toasterService.showError('Failed to cancel order'));
  }

  /** Navigates to the specified photo order */
  private navigateToOrder(photoOrder: PhotoOrder) {
    this.router.navigate(['/manage-media', photoOrder._id], { queryParams: { photographerId: this.selectedPhotographer()?.id } });
  }

  /** Parses the query params from the url and patches the form values */
  private parseQueryParams(params) {
    this.filtersForm.patchFromQueryParams(params);
  }

  /** Updates the query parameters in the current url */
  private updateQueryParams() {
    const params = this.filtersForm.getQueryParams();
    this.router.navigate([], { relativeTo: this.route, queryParams: params });
  }

  onPageChanged(event: PageEvent) {
    this.pageRequestForm.pageSize.setValue(event.pageSize);
    this.pageRequestForm.pageNumber.setValue(event.pageIndex + 1);
  }
}
